import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { TrendingUp, Lightbulb, Create } from '@mui/icons-material';
import axios from 'axios';
import { useCompany } from '../contexts/CompanyContext';

interface TrendingKeyword {
  keyword: string;
  score: number;
  category?: string;
  trend?: string;
}

interface PostIdea {
  id: string | number;
  title: string;
  keyword: string;
  type: string;
  estimatedEngagement?: number;
}

interface ImageThumbnail {
  url: string;
  seed: number;
}

interface GeneratedContent {
  id: string | number;
  title: string;
  content: string;
  type: string;
  createdAt: string;
  imageUrl?: string;
  imagePrompt?: string;
  imageThumbnails?: ImageThumbnail[];
}

const ContentCreation: React.FC = () => {
  const [trendingKeywords, setTrendingKeywords] = useState<TrendingKeyword[]>([]);
  const [postIdeas, setPostIdeas] = useState<PostIdea[]>([]);
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const [loadingIdeas, setLoadingIdeas] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [loadingFinalImage, setLoadingFinalImage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contentDialog, setContentDialog] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState<PostIdea | null>(null);
  const [contentType, setContentType] = useState('instagram');
  const [postTone, setPostTone] = useState('professional');
  const [imageStyle, setImageStyle] = useState('digital-art');
  const [selectedThumbnail, setSelectedThumbnail] = useState<number | null>(null);
  const [cacheInfo, setCacheInfo] = useState<{ cached: boolean; cachedAt?: string; expiresAt?: string } | null>(null);

  const { selectedCompany } = useCompany();

  const fetchTrendingKeywords = async (forceRefresh: boolean = false) => {
    if (!selectedCompany) {
      setError('Please select a company first');
      return;
    }

    try {
      setLoadingKeywords(true);
      setError(null);
      console.log('Fetching trending keywords...', forceRefresh ? '(force refresh)' : '');
      
      const response = await axios.get('/api/trends/keywords', {
        params: {
          companyId: selectedCompany.id,
          industry: selectedCompany.industry,
          refresh: forceRefresh
        }
      });
      console.log('Keywords response:', response.data);
      
      // New response format: { keywords: [], cached: boolean, cachedAt?: string, expiresAt?: string }
      setTrendingKeywords(response.data.keywords || response.data);
      setCacheInfo({
        cached: response.data.cached || false,
        cachedAt: response.data.cachedAt,
        expiresAt: response.data.expiresAt
      });
    } catch (err: any) {
      console.error('Error fetching keywords:', err);
      setError('Failed to fetch trending keywords');
    } finally {
      setLoadingKeywords(false);
    }
  };

  const generatePostIdeas = async () => {
    if (!selectedCompany || trendingKeywords.length === 0) {
      setError('Please fetch trending keywords first');
      return;
    }

    try {
      setLoadingIdeas(true);
      setError(null);
      console.log('Generating post ideas...');
      console.log('Request payload:', {
        keywords: trendingKeywords,
        companyInfo: selectedCompany
      });
      
      const response = await axios.post('/api/trends/ideas', {
        keywords: trendingKeywords,
        companyInfo: selectedCompany
      });
      
      console.log('Ideas response:', response.data);
      setPostIdeas(response.data);
      
      if (!response.data || response.data.length === 0) {
        console.log('No post ideas received from API');
        setError('No post ideas received from API');
      }
    } catch (err: any) {
      console.error('Error generating ideas:', err);
      setError('Failed to generate post ideas');
    } finally {
      setLoadingIdeas(false);
    }
  };

  const openContentDialog = (idea: PostIdea) => {
    setSelectedIdea(idea);
    setContentDialog(true);
    setGeneratedContent(null); // Reset any previous content
  };

  const generateContent = async () => {
    if (!selectedIdea) return;
    
    try {
      setLoadingContent(true);
      setError(null);
      
      // Generate text content
      const response = await axios.post('/api/content/generate', {
        idea: selectedIdea,
        companyInfo: selectedCompany,
        companyId: selectedCompany?.id,
        contentType,
        tone: postTone
      });
      
      const content = response.data;
      
      // Check if we need to generate image thumbnails
      const needsImage = ['instagram', 'facebook', 'linkedin', 'blog_post'].includes(contentType);
      
      // TEMPORARILY DISABLED - Image generation under refinement
      // Users can add images manually using Canva/Photoshop
      if (false && needsImage) {
        try {
          setLoadingImage(true);
          
          // DALL-E 3 VIRAL-FOCUSED PROMPTS
          // Social posts = attention-grabbing graphics WITH text
          // Blog posts = scroll-stopping images WITHOUT text
          const isTextPost = ['instagram', 'facebook', 'linkedin'].includes(contentType);
          
          let imagePrompt = '';
          
          if (isTextPost) {
            // Ultra-simple product photography - LITERAL and SPECIFIC
            imagePrompt = `A single piece of industrial equipment photographed on a plain white background.

ULTRA SIMPLE - LIKE A STOCK PHOTO:
- Just ONE object
- Centered in frame
- Plain solid white or light gray background
- No environment, no context, no scene
- Professional studio lighting
- Like an Amazon product listing photo
- SIMPLE. BORING. CLEAN.

Style: Stock photography
Composition: ONE object, centered, plain background
Lighting: Even, professional studio lighting

Think: Industrial equipment catalog photo or stock photography website

STRICT RULES:
- ONLY photograph ONE single object
- NOTHING ELSE in the image
- Plain background only
- NO scenes, NO environments, NO context
- NO people, NO hands, NO workspace
- NO decorations, NO additional elements
- Just the object on a plain background
- Like a product you'd buy on Amazon

Example: A dust collection unit sitting on white background. That's it. Nothing else.

Related to: ${selectedIdea?.keyword} in ${selectedCompany?.industry} - pick ONE simple piece of relevant equipment.`;
          } else {
            // Blog post - Clean technical photography (NO text)
            imagePrompt = `Clean, professional technical photography for a blog about "${selectedIdea?.keyword}" in ${selectedCompany?.industry}.

JUST A PHOTO - No text, no Instagram mockup, no UI elements.

Visual Style:
- Real ${selectedCompany?.industry} equipment or workspace
- Clean workshop, lab, or industrial setting
- Close-up of tools, machines, or processes
- Natural good lighting - professional but not dramatic
- Authentic workplace photography

CRITICAL:
❌ NO text or words anywhere in the image
❌ NO Instagram frames or UI mockups
❌ NO concept art or 3D renders
❌ NO surreal or sci-fi elements
❌ NO dramatic lighting effects

✅ Just a clean, professional photo of real equipment
✅ Like a Markforged or Haas Automation product catalog photo
✅ Authentic technical photography
✅ Something you'd see in an industry magazine

Focus: ${selectedIdea?.keyword} - show the actual tool, machine, or workspace.`;
          }
          
          // DALL-E 3 doesn't use negative prompts or style presets the same way
          const thumbnailResponse = await axios.post('/api/images/generate-thumbnails', {
            prompt: imagePrompt,
            companyId: selectedCompany?.id,
            contentType,
            style: imageStyle,
            count: 4  // DALL-E will generate 4 variations
          });
          
          content.imageThumbnails = thumbnailResponse.data.thumbnails;
          content.imagePrompt = imagePrompt;
        } catch (imgErr: any) {
          console.error('Error generating image thumbnails:', imgErr);
          // Don't fail the whole operation if image generation fails
          setError('Content generated, but image generation failed. You can regenerate to try again.');
        } finally {
          setLoadingImage(false);
        }
      }
      
      setGeneratedContent(content);
    } catch (err: any) {
      console.error('Error generating content:', err);
      setError('Failed to generate content');
    } finally {
      setLoadingContent(false);
    }
  };

  const handleSelectThumbnail = async (index: number) => {
    if (!generatedContent?.imageThumbnails || !selectedCompany) return;
    
    setSelectedThumbnail(index);
    setLoadingFinalImage(true);
    
    try {
      const thumbnail = generatedContent.imageThumbnails[index];
      
      // Generate final high-res version of selected thumbnail
      // DALL-E 3 finalize - regenerate with same prompt for HD quality
      const finalImageResponse = await axios.post('/api/images/finalize', {
        prompt: generatedContent.imagePrompt,
        seed: thumbnail.seed,  // Not used by DALL-E but kept for compatibility
        companyId: selectedCompany.id,
        contentType,
        width: 1024,
        height: 1024
      });
      
      setGeneratedContent({
        ...generatedContent,
        imageUrl: finalImageResponse.data.imageUrl
      });
    } catch (err: any) {
      console.error('Error finalizing image:', err);
      setError('Failed to generate final image. Please try selecting another thumbnail.');
    } finally {
      setLoadingFinalImage(false);
    }
  };

  const handleDownloadImage = () => {
    if (!generatedContent?.imageUrl) return;
    
    const link = document.createElement('a');
    link.href = generatedContent.imageUrl;
    link.download = `storyforge-${contentType}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCloseContentDialog = () => {
    setContentDialog(false);
    setSelectedIdea(null);
    setGeneratedContent(null);
    setSelectedThumbnail(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Content Creation
      </Typography>
      
      {!selectedCompany && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please select a company from the dropdown to start creating content.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Step 1: Get Trending Keywords */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TrendingUp sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                Step 1: Get Trending Keywords
              </Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Curated trending keywords relevant to your industry
            </Typography>
            
            {cacheInfo && cacheInfo.cached && cacheInfo.cachedAt && (
              <Alert severity="success" sx={{ mb: 2, fontSize: '0.875rem' }}>
                <strong>Cached Data</strong> - Last updated: {new Date(cacheInfo.cachedAt).toLocaleDateString()} at {new Date(cacheInfo.cachedAt).toLocaleTimeString()}
                <br />
                <Typography variant="caption">Cache valid for 7 days. Click "Refresh" to update now.</Typography>
              </Alert>
            )}
            
            {(!cacheInfo || !cacheInfo.cached) && trendingKeywords.length > 0 && (
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.875rem' }}>
                <strong>Fresh Data</strong> - Just generated and cached for 7 days
              </Alert>
            )}
            
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={() => fetchTrendingKeywords(false)}
                disabled={loadingKeywords || !selectedCompany}
                startIcon={loadingKeywords ? <CircularProgress size={20} /> : <TrendingUp />}
              >
                Get Trending Keywords
              </Button>
              
              {trendingKeywords.length > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => fetchTrendingKeywords(true)}
                  disabled={loadingKeywords || !selectedCompany}
                  startIcon={loadingKeywords ? <CircularProgress size={20} /> : <TrendingUp />}
                >
                  Refresh
                </Button>
              )}
            </Box>

            <Box>
              {trendingKeywords.map((keyword, index) => (
                <Chip
                  key={index}
                  label={`${keyword.keyword} (${keyword.score})`}
                  sx={{ m: 0.5 }}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Step 2: Generate Post Ideas */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Lightbulb sx={{ mr: 1, color: 'secondary.main' }} />
              <Typography variant="h6">
                Step 2: Generate Post Ideas
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={generatePostIdeas}
                disabled={loadingIdeas || trendingKeywords.length === 0}
                startIcon={loadingIdeas ? <CircularProgress size={20} /> : <Lightbulb />}
              >
                Generate Post Ideas
              </Button>
              
              {postIdeas.length > 0 && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={generatePostIdeas}
                  disabled={loadingIdeas || trendingKeywords.length === 0}
                  startIcon={loadingIdeas ? <CircularProgress size={20} /> : <Lightbulb />}
                >
                  Refresh Ideas
                </Button>
              )}
            </Box>

            <Box>
              {postIdeas.length === 0 && !loadingIdeas ? (
                <Typography variant="body2" color="text.secondary">
                  Generate trending keywords first, then click "Generate Post Ideas"
                </Typography>
              ) : null}
            </Box>
          </Paper>
        </Grid>

        {/* Step 3: Choose Post Idea */}
        {postIdeas.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Step 3: Choose Post Idea
            </Typography>
            <Grid container spacing={2}>
              {postIdeas.map((idea) => (
                <Grid item xs={12} sm={6} md={4} key={idea.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" component="h3" gutterBottom>
                        {idea.title}
                      </Typography>
                      <Chip 
                        label={idea.keyword} 
                        size="small" 
                        color="primary" 
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Type: {idea.type}
                      </Typography>
                      {idea.estimatedEngagement && (
                        <Typography variant="body2" color="text.secondary">
                          Est. Engagement: {idea.estimatedEngagement}
                        </Typography>
                      )}
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Create />}
                        onClick={() => openContentDialog(idea)}
                      >
                        Create Content
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
      </Grid>

      {/* Content Generation Dialog */}
      <Dialog 
        open={contentDialog} 
        onClose={handleCloseContentDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {generatedContent ? 'Generated Content' : 'Create Content'}: {selectedIdea?.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Content Type</InputLabel>
              <Select
                value={contentType}
                label="Content Type"
                onChange={(e) => setContentType(e.target.value)}
                disabled={loadingContent || loadingImage}
              >
                <MenuItem value="instagram">📸 Instagram Post (with image, 2200 chars max)</MenuItem>
                <MenuItem value="facebook">👥 Facebook Post (with image, engaging)</MenuItem>
                <MenuItem value="linkedin">💼 LinkedIn Post (with image, professional)</MenuItem>
                <MenuItem value="twitter">𝕏 X/Twitter (280 chars, text only)</MenuItem>
                <MenuItem value="blog_post">📝 Blog Post (with featured image)</MenuItem>
                <MenuItem value="email">✉️ Email Campaign</MenuItem>
                <MenuItem value="ad_copy">📢 Ad Copy (Short, compelling)</MenuItem>
                <MenuItem value="subject_line">💡 Subject Line / Headline</MenuItem>
              </Select>
            </FormControl>

            {/* Post Tone/Voice Selector */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Post Tone</InputLabel>
              <Select
                value={postTone}
                label="Post Tone"
                onChange={(e) => setPostTone(e.target.value)}
                disabled={loadingContent || loadingImage}
              >
                <MenuItem value="professional">📝 Professional (Business-focused, formal)</MenuItem>
                <MenuItem value="conversational">💬 Conversational (Friendly, casual)</MenuItem>
                <MenuItem value="humorous">🎭 Humorous (Witty, entertaining)</MenuItem>
                <MenuItem value="authoritative">🎓 Authoritative (Expert, credible)</MenuItem>
                <MenuItem value="inspirational">✨ Inspirational (Motivational, uplifting)</MenuItem>
                <MenuItem value="educational">📚 Educational (Informative, teaching)</MenuItem>
                <MenuItem value="bold">🔥 Bold (Confident, provocative)</MenuItem>
                <MenuItem value="empathetic">🤝 Empathetic (Understanding, supportive)</MenuItem>
              </Select>
            </FormControl>

            {/* Image Style Selector - only show for content types that need images */}
            {['instagram', 'facebook', 'linkedin', 'blog_post'].includes(contentType) && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Image Style</InputLabel>
                <Select
                  value={imageStyle}
                  label="Image Style"
                  onChange={(e) => setImageStyle(e.target.value)}
                  disabled={loadingContent || loadingImage}
                >
                  <MenuItem value="photographic">📷 Photographic (Realistic, professional photos)</MenuItem>
                  <MenuItem value="digital-art">🎨 Digital Art (Modern, vibrant illustrations)</MenuItem>
                  <MenuItem value="cinematic">🎬 Cinematic (Dramatic, movie-like quality)</MenuItem>
                  <MenuItem value="3d-model">🧊 3D Render (Clean, modern 3D graphics)</MenuItem>
                  <MenuItem value="analog-film">📸 Analog Film (Vintage, film photography)</MenuItem>
                  <MenuItem value="line-art">✏️ Line Art (Minimal, sketch-style)</MenuItem>
                  <MenuItem value="low-poly">🔷 Low Poly (Geometric, abstract)</MenuItem>
                  <MenuItem value="fantasy-art">🌟 Fantasy Art (Artistic, imaginative)</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>

          {loadingContent || loadingImage ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {loadingContent && !loadingImage && `Generating ${
                  contentType === 'instagram' ? 'Instagram post' :
                  contentType === 'facebook' ? 'Facebook post' :
                  contentType === 'linkedin' ? 'LinkedIn post' :
                  contentType === 'twitter' ? 'X/Twitter post' :
                  contentType === 'blog_post' ? 'blog post' :
                  contentType === 'email' ? 'email campaign' :
                  contentType === 'ad_copy' ? 'ad copy' :
                  'subject line'} with AI...`}
                {loadingImage && 'Generating images with DALL-E 3...'}
              </Typography>
            </Box>
          ) : generatedContent ? (
            <Box>
              {/* Show thumbnail selection if thumbnails exist and no final image */}
              {generatedContent.imageThumbnails && !generatedContent.imageUrl && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Choose Your Favorite Image:
                  </Typography>
                  <Grid container spacing={2}>
                    {generatedContent.imageThumbnails.map((thumbnail, index) => (
                      <Grid item xs={6} key={index}>
                        <Paper
                          elevation={selectedThumbnail === index ? 8 : 2}
                          sx={{
                            p: 1,
                            cursor: loadingFinalImage ? 'wait' : 'pointer',
                            border: selectedThumbnail === index ? '3px solid #1976d2' : 'none',
                            transition: 'all 0.3s',
                            '&:hover': {
                              elevation: 4,
                              transform: 'scale(1.02)'
                            }
                          }}
                          onClick={() => !loadingFinalImage && handleSelectThumbnail(index)}
                        >
                          <img 
                            src={thumbnail.url} 
                            alt={`Thumbnail ${index + 1}`} 
                            style={{ 
                              width: '100%', 
                              height: 'auto',
                              borderRadius: '4px',
                              display: 'block'
                            }} 
                          />
                          <Button
                            fullWidth
                            variant={selectedThumbnail === index ? 'contained' : 'outlined'}
                            size="small"
                            sx={{ mt: 1 }}
                            disabled={loadingFinalImage}
                          >
                            {selectedThumbnail === index && loadingFinalImage 
                              ? 'Generating...' 
                              : selectedThumbnail === index 
                                ? 'Selected' 
                                : 'Select This'}
                          </Button>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                  {loadingFinalImage && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                      <Typography>Generating final high-resolution image...</Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Show final image if available */}
              {generatedContent.imageUrl && (
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                  <Paper elevation={3} sx={{ p: 2, display: 'inline-block' }}>
                    <img 
                      src={generatedContent.imageUrl} 
                      alt={`${contentType} for ${generatedContent.title}`} 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '500px', 
                        borderRadius: '8px',
                        display: 'block'
                      }} 
                    />
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Button
                        variant="contained"
                        startIcon={<Create />}
                        onClick={handleDownloadImage}
                      >
                        Download Image
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setGeneratedContent({
                            ...generatedContent,
                            imageUrl: undefined
                          });
                          setSelectedThumbnail(null);
                        }}
                      >
                        Choose Different
                      </Button>
                    </Box>
                  </Paper>
                  {generatedContent.imagePrompt && (
                    <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                      Prompt: {generatedContent.imagePrompt}
                    </Typography>
                  )}
                </Box>
              )}

              <Typography variant="h6" gutterBottom>
                {generatedContent.title}
              </Typography>
              <TextField
                multiline
                rows={12}
                fullWidth
                value={generatedContent.content}
                variant="outlined"
                InputProps={{
                  readOnly: true,
                }}
              />
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Generated at: {new Date(generatedContent.createdAt).toLocaleString()}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Select a content type above and click "Generate" to create AI-powered content
                {['instagram', 'facebook', 'linkedin', 'blog_post'].includes(contentType) && (
                  <><br /><strong>Note:</strong> An image will be automatically generated for this content type</>
                )}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContentDialog}>Close</Button>
          {generatedContent ? (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setGeneratedContent(null);
                }}
              >
                Change Type & Regenerate
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  navigator.clipboard.writeText(generatedContent.content);
                }}
              >
                Copy to Clipboard
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={generateContent}
              disabled={loadingContent}
              startIcon={loadingContent ? <CircularProgress size={20} /> : <Create />}
            >
              Generate Content
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentCreation;

